import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Dashboard.css";
import { Grid, Typography } from "@mui/material";
import { Calendar, Users, Wallet, TrendingUp, Activity } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart
} from "recharts";
import QuickAndFullRangeDatePicker from "../../CommonComponents/QuickAndFullRangeDatePicker";
import ENDPOINTS from "../../Constants/ApiEndpoints";

const StatCard = ({ icon: Icon, value, label, trend, colorScheme }) => {
  const getScheme = () => {
    switch (colorScheme) {
      case 'green':
        return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' };
      case 'indigo':
        return { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe' };
      default:
        return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
    }
  };

  const scheme = getScheme();

  return (
    <div className="stat-card-enterprise">
      <div className="stat-header">
        <div className="stat-icon-wrapper" style={{ backgroundColor: scheme.bg, color: scheme.color, borderColor: scheme.border }}>
          <Icon size={22} />
        </div>
        {trend && <span className="stat-trend-badge">{trend}</span>}
      </div>

      <div className="stat-body">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-label">{label}</p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const [opCount, setOpCount] = useState(0);
  const [outpatientsCount, setOutpatientsCount] = useState(0);
  const [collectionAmount, setCollectionAmount] = useState(0);

  const [dailyAppointmentsData, setDailyAppointmentsData] = useState([]);
  const [doctorMetricsData, setDoctorMetricsData] = useState([]);

  useEffect(() => {
    const today = new Date();
    fetchMetricsFromAPI(today, today);
    fetchDailyAppointmentCounts(today, today);
    fetchDoctorAppointments(today, today);
  }, []);

  function formatDateToLocalYMD(date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  const fetchMetricsFromAPI = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const [opRes, oppatientsRes, collectionRes] = await Promise.all([
        axios.get(ENDPOINTS.GET_TOTAL_OP_APPOINTMENTS_COUNT_BY_DATE, { params: { fromDate, toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_OUTPATIENTS_COUNT_BY_DATE, { params: { fromDate, toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'OUTPATIENT_INVOICE', fromDate, toDate } }),
      ]);

      setOpCount(opRes.data || 0);
      setOutpatientsCount(oppatientsRes.data || 0);
      setCollectionAmount(collectionRes.data || 0);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setOpCount(0);
      setOutpatientsCount(0);
      setCollectionAmount(0);
    }
  };

  const fetchDailyAppointmentCounts = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_DAILY_APPOINTMENTS_COUNT, {
        params: { fromDate, toDate },
      });
      setDailyAppointmentsData(response.data || []);
    } catch (error) {
      console.error("Error fetching daily appointment counts:", error);
      setDailyAppointmentsData([]);
    }
  };

  const fetchDoctorAppointments = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_TOTAL_OP_APPOINTMENTS_COUNTOFDOCTOR_BYDATE, {
        params: { fromDate, toDate },
      });
      setDoctorMetricsData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor appointments:", error);
      setDoctorMetricsData([]);
    }
  };

  const handleDateRangeChange = (start, end, callback) => {
    callback(start, end);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-chart-tooltip">
          <p className="tooltip-title">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="tooltip-item">
              <span className="tooltip-dot" style={{ backgroundColor: entry.color }} />
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-wrapper">
      {/* Dashboard Top Banner */}
      <div className="dashboard-hero-header">
        <div>
          <h2 className="dashboard-title">Outpatient Analytics & Metrics</h2>
          <p className="dashboard-subtitle">Real-time Overview & Clinical Performance Metrics</p>
        </div>
        <div className="live-status-pill">
          <span className="live-dot" /> Live Data Sync
        </div>
      </div>

      {/* Date Range Selector Bar */}
      <div className="dashboard-filter-bar">
        <span className="filter-bar-label">📅 Metric Date Range:</span>
        <QuickAndFullRangeDatePicker
          onDateChange={(start, end) => handleDateRangeChange(start, end, fetchMetricsFromAPI)}
        />
      </div>

      {/* KPI Stats Grid */}
      <div className="stats-cards-grid">
        <StatCard
          icon={Calendar}
          value={opCount}
          label="Total OP Appointments"
          trend="📈 Active"
          colorScheme="blue"
        />
        <StatCard
          icon={Users}
          value={outpatientsCount}
          label="Total Outpatients"
          trend="💳 Real-time"
          colorScheme="green"
        />
        <StatCard
          icon={Wallet}
          value={`₹ ${collectionAmount.toLocaleString()}`}
          label="Total Collection"
          trend="💳 Real-time"
          colorScheme="indigo"
        />
      </div>

      {/* Analytics Chart Container */}
      <div className="analytics-container-card">
        <div className="analytics-card-header">
          <div className="chart-tabs-nav">
            <button
              className={`chart-tab-btn ${selectedTab === 0 ? 'active' : ''}`}
              onClick={() => setSelectedTab(0)}
            >
              <TrendingUp size={16} /> Appointment Trends
            </button>
            <button
              className={`chart-tab-btn ${selectedTab === 1 ? 'active' : ''}`}
              onClick={() => setSelectedTab(1)}
            >
              <Activity size={16} /> Doctor Appointment Trends
            </button>
          </div>
        </div>

        {selectedTab === 0 && (
          <div className="chart-body">
            <div className="chart-toolbar">
              <h3 className="chart-section-heading">Daily Appointment Trends</h3>
              <QuickAndFullRangeDatePicker
                onDateChange={(start, end) => handleDateRangeChange(start, end, fetchDailyAppointmentCounts)}
              />
            </div>

            {dailyAppointmentsData.length === 0 ? (
              <div className="no-chart-data">
                <Calendar size={48} color="#94a3b8" />
                <p>No appointment trend data available for the selected date range.</p>
              </div>
            ) : (
              <div className="chart-responsive-box">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={dailyAppointmentsData.map(({ date, paidCount, freeCount }) => ({
                    date,
                    Paid: paidCount,
                    Free: freeCount,
                  }))}>
                    <defs>
                      <linearGradient id="paidGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="freeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Area type="monotone" dataKey="Paid" stroke="#2563eb" strokeWidth={2.5} fillOpacity={1} fill="url(#paidGrad)" />
                    <Area type="monotone" dataKey="Free" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#freeGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {selectedTab === 1 && (
          <div className="chart-body">
            <div className="chart-toolbar">
              <h3 className="chart-section-heading">Doctor Appointments Distribution</h3>
              <QuickAndFullRangeDatePicker
                onDateChange={(start, end) => handleDateRangeChange(start, end, fetchDoctorAppointments)}
              />
            </div>

            {doctorMetricsData.length === 0 ? (
              <div className="no-chart-data">
                <Users size={48} color="#94a3b8" />
                <p>No doctor appointment data available for the selected date range.</p>
              </div>
            ) : (
              <div className="chart-responsive-box">
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart
                    data={doctorMetricsData.map(({ doctorName, appointmentCount }) => ({
                      name: doctorName,
                      Appointments: appointmentCount,
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#1d4ed8" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={40} stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="Appointments" fill="url(#barGrad)" barSize={36} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;